import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, GripVertical } from "lucide-react";
import { GearItem } from "@/types/gear";
import { formatCurrency } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import { useCurrency } from "@/contexts/CurrencyContext";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableGearRowProps {
  item: GearItem;
  index: number;
  onEdit?: (item: GearItem) => void;
  onDelete?: (id: string) => void;
  showCost?: boolean;
  showBudget?: boolean;
  allowEdit?: boolean;
  canReorder?: boolean;
}

export function SortableGearRow({
  item,
  index,
  onEdit,
  onDelete,
  showCost = true,
  showBudget = true,
  allowEdit = true,
  canReorder = false
}: SortableGearRowProps) {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const total = (item.quantity || 0) * (item.cost_per_unit || 0) * (item.days_needed || 1);
  
  const getRowClass = () => {
    if (!item.color) return index % 2 === 0 ? 'bg-transparent' : 'bg-primary/5';
    const baseClass = index % 2 === 0 ? 'bg-transparent' : 'bg-primary/5';
    switch (item.color) {
      case 'blue': return 'bg-blue-500/20 border-blue-500/30';
      case 'green': return 'bg-green-500/20 border-green-500/30';
      case 'yellow': return 'bg-yellow-500/20 border-yellow-500/30';
      case 'red': return 'bg-red-500/20 border-red-500/30';
      case 'purple': return 'bg-purple-500/20 border-purple-500/30';
      default: return baseClass;
    }
  };

  return (
    <TableRow 
      ref={setNodeRef}
      style={style}
      className={getRowClass()}
    >
      {allowEdit && canReorder && (
        <TableCell className="rounded-none p-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/50 rounded"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </TableCell>
      )}
      <TableCell className="rounded-none">
        <div>
          <div className="font-medium">{item.name}</div>
          {item.notes && (
            <div className="text-sm text-muted-foreground mt-0.5">{item.notes}</div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right rounded-none">{item.quantity || 0}</TableCell>
      {showCost && showBudget && (
        <>
          <TableCell className="text-right rounded-none">
            {item.cost_per_unit ? formatCurrency(item.cost_per_unit, currency) : '-'}
          </TableCell>
          <TableCell className="text-right rounded-none">{item.days_needed || 1}</TableCell>
          <TableCell className="text-right rounded-none">{formatCurrency(total, currency)}</TableCell>
        </>
      )}
      <TableCell className="rounded-none">
        {item.vendors?.name || '-'}
      </TableCell>
      {allowEdit && (
        <TableCell className="rounded-none">
          <div className="flex justify-end gap-2">
            {onEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(item)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onDelete(item.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}